import React, { useState, useEffect } from 'react';
import { Wrench, Search, Filter, Edit, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/env.js';

const MechanicManagement = () => {
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState('all');

  useEffect(() => {
    loadMechanics();
  }, [searchQuery, filterActive]);

  const loadMechanics = async () => {
    try {
      setLoading(true);
      let url = `${SUPABASE_URL}/rest/v1/mechanics?select=*&order=created_at.desc&limit=100`;
      
      if (filterActive !== 'all') {
        url += `&active=eq.${filterActive === 'active'}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
      });
      
      const data = await response.json();
      let filtered = data || [];
      
      if (searchQuery) {
        filtered = filtered.filter(m => 
          m.mechanic_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.number?.includes(searchQuery)
        );
      }
      
      setMechanics(filtered);
    } catch (error) {
      console.error('Error loading mechanics:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (mechanicId, currentStatus) => {
    try {
      const response = await fetch(`${SUPABASE_URL}/rest/v1/mechanics?id=eq.${mechanicId}`, {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation',
        },
        body: JSON.stringify({ active: !currentStatus }),
      });
      
      if (response.ok) {
        loadMechanics();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Mechanic Management</h1>
        <p className="text-gray-600 mt-1">Manage mechanic details and service areas</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, code, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterActive('all')}
              className={`px-4 py-2 rounded-lg ${filterActive === 'all' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              All
            </button>
            <button
              onClick={() => setFilterActive('active')}
              className={`px-4 py-2 rounded-lg ${filterActive === 'active' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              Active
            </button>
            <button
              onClick={() => setFilterActive('inactive')}
              className={`px-4 py-2 rounded-lg ${filterActive === 'inactive' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
            >
              Inactive
            </button>
          </div>
        </div>
      </div>

      {/* Mechanics List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : mechanics.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No mechanics found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timing</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service Areas</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {mechanics.map((mechanic) => (
                  <tr key={mechanic.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{mechanic.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{mechanic.mechanic_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{mechanic.number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{mechanic.timing || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {mechanic.service_areas?.length || 0} area(s)
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${mechanic.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {mechanic.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => toggleStatus(mechanic.id, mechanic.active)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                      >
                        {mechanic.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MechanicManagement;

